import * as React from 'react'
import * as Yup from 'yup'
import * as RT from '../../config/routes'
import { Formik, Form } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { setIsStored } from '../../redux/slices/Alerts'
import { setUsername } from '../../redux/slices/Auth/login'
import { styled } from '@mui/material/styles'
import {
  Grid,
  Box,
  Button,
  IconButton,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Typography as MIUTypography, InputAdornment } from '@mui/material'
import { Lock, AccountCircle, Visibility, VisibilityOff } from '@mui/icons-material'
import { updateUser, getUserMe } from '../../services'
import { schema } from '../../utils/schemaValidation'
import AlertCustom from '../../components/Alerts'
import Textfield from '../../components/FormsUI/Textfield'

// Fondo con gradiente usando la paleta de colores del dashboard
const BackgroundContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: '100vh',
  background: `linear-gradient(135deg, 
    #00070A 0%, 
    #282B1B 20%, 
    #2B3B28 40%, 
    #4E6A5B 60%, 
    #A68D4A 80%, 
    #E0DED1 100%
  )`,
  backgroundAttachment: 'fixed',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  overflow: 'auto',
  zIndex: 0,
  // Patrón adicional para más textura
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, #4E6A5B20 0%, transparent 50%),
                 radial-gradient(circle at 80% 20%, #A68D4A15 0%, transparent 50%),
                 radial-gradient(circle at 40% 40%, #2B3B2810 0%, transparent 50%)`,
    zIndex: 1,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}))

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 20px 40px rgba(0, 7, 10, 0.3)',
  backdropFilter: 'blur(10px)',
  background: 'rgba(224, 222, 209, 0.95)',
  border: '1px solid rgba(166, 141, 74, 0.3)',
  maxWidth: 600,
  width: '100%',
  margin: 'auto',
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    maxWidth: 500,
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '95%',
    padding: theme.spacing(2.5),
    margin: theme.spacing(1),
    borderRadius: theme.spacing(1.5),
  },
}))

const Typography = styled(MIUTypography)(({ theme, istitle }) => ({
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: istitle ? '1.75rem' : '1.125rem',
  fontWeight: istitle ? 600 : 450,
  lineHeight: istitle ? '2.125rem' : '1.5rem',
  color: istitle ? '#282B1B' : '#4E6A5B',
  textAlign: 'center',
  marginBottom: istitle ? theme.spacing(3) : 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: istitle ? '1.5rem' : '1rem',
    lineHeight: istitle ? '1.875rem' : '1.375rem',
  },
}))

const ButtonCustom = styled(Button)(({ theme }) => ({
  backgroundColor: '#4E6A5B',
  color: '#E0DED1',
  textTransform: 'capitalize',
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '22px',
  fontSize: '1rem',
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.spacing(1),
  boxShadow: '0 4px 12px rgba(40, 43, 27, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#2B3B28',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(40, 43, 27, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 2px 8px rgba(40, 43, 27, 0.3)',
  },
  '&:disabled': {
    backgroundColor: '#A68D4A',
    color: '#E0DED1',
    transform: 'none',
    boxShadow: 'none',
    opacity: 0.7,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1.25, 2.5),
  },
}))

const ProfilePage = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const alertRef = React.useRef()
  const [showPassword, setShowPassword] = React.useState(false)
  const [updated, setUpdated] = React.useState(false)
  const [INITIAL_FORM_STATE, SET_INITIAL_FORM_STATE] = React.useState()
  const { isStored } = useSelector(state => state.alertsGlobal)
  const TOKEN = `Bearer ${window.localStorage.getItem('ArbolEc_token')}`

  const FORM_VALIDATION = Yup.object().shape({
    ...schema.USERNAME,
    ...schema.LASTNAME,
    ...schema.EMAIL,
    ...schema.PASSWORD,
    ...schema.PASSWORD_CONFIRMATION,
  })

  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const handleMouseDownPassword = event => event.preventDefault()

  const handleClickLogin = async ({ username, lastname, email, password }) => {
    let resultSave = { stored: true }
    const data = {
      nombre: username,
      apellido: lastname,
      email,
      contrasena: Boolean(password) ? password : undefined,
    }

    const response = await updateUser(data, TOKEN)
    if (!response?.update) {
      return { stored: false, message: response?.message }
    }
    setUpdated(true)
    return resultSave
  }

  React.useEffect(() => {
    if (INITIAL_FORM_STATE) return
    getUserMe(TOKEN)
      .then(data => {
        const { user } = data
        SET_INITIAL_FORM_STATE({
          username: user?.nombre ?? '',
          lastname: user?.apellido ?? '',
          email: user?.email ?? '',
          password: '',
          password_confirmation: '',
        })
      })
      .catch(err => console.log('Error :>> ', err))
  }, [INITIAL_FORM_STATE, TOKEN])

  React.useEffect(() => {
    if (!Boolean(isStored)) return
    dispatch(setUsername(null))
    alertRef?.current?.openAlert(true)
    alertRef?.current?.goRute(RT.MAIN)
  }, [isStored, dispatch])

  if (!INITIAL_FORM_STATE) return null

  return (
    <BackgroundContainer>
      <Container maxWidth="md">
        <FormPaper elevation={0}>
          <Typography istitle="true">Actualizar Datos</Typography>

          <Formik
            initialValues={{ ...INITIAL_FORM_STATE }}
            validationSchema={FORM_VALIDATION}
            onSubmit={async values => {
              const itsStored = await handleClickLogin(values)
              dispatch(setIsStored(itsStored))
            }}>
            <Form>
              <Grid container spacing={isMobile ? 2 : 3}>
                {/* Nombre y Apellido en una fila en desktop, separados en mobile */}
                <Grid item xs={12} sm={6}>
                  <Textfield name="username" label="Nombre" fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Textfield name="lastname" label="Apellido" fullWidth />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <Textfield
                    name="email"
                    label="Correo electrónico"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountCircle sx={{ color: '#4E6A5B' }} />
                        </InputAdornment>
                      ),
                    }}
                    placeholder="user@example.com"
                  />
                </Grid>

                {/* Contraseña */}
                <Grid item xs={12}>
                  <Textfield
                    name="password"
                    label="Nueva contraseña"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: '#4E6A5B' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            sx={{ color: '#4E6A5B' }}>
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Ingrese nueva contraseña"
                  />
                </Grid>

                {/* Confirmar contraseña */}
                <Grid item xs={12}>
                  <Textfield
                    name="password_confirmation"
                    label="Confirmar contraseña"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: '#4E6A5B' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            sx={{ color: '#4E6A5B' }}>
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Confirme la nueva contraseña"
                  />
                </Grid>

                {/* Botón de actualizar */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <ButtonCustom
                      variant="contained"
                      fullWidth
                      type="submit"
                      disabled={updated}
                      size={isMobile ? 'medium' : 'large'}>
                      {updated ? 'Actualizado' : 'Actualizar Datos'}
                    </ButtonCustom>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </Formik>
        </FormPaper>
      </Container>

      <AlertCustom
        ref={alertRef}
        severity={isStored?.stored !== true ? 'warning' : 'success'}>
        {isStored?.stored !== true
          ? isStored?.message
          : 'Datos actualizados exitosamente.'}
      </AlertCustom>
    </BackgroundContainer>
  )
}

export default ProfilePage
